import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { bcStates } from 'common/common/states';
import { bcSessionKeys } from 'common/common/constants/session-keys';
import { LogoutSubscriber } from '../../shared/services/subscribers/logout-subscriber.service';
import { CoolSessionStorage } from '@angular-cool/storage';
import { BcAuth } from 'common/auth/auth.service';
import { Subscription } from 'rxjs';
import { StateServiceWrapper } from 'shared/services/state-service-wrapper/state-service-wrapper';
import { ActivatedRoute } from '@angular/router';
import { IdentityLoginConfigurationModel } from 'common/api/models/identity-login-configuration.model';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit, OnDestroy {
  private logoutSubscription: Subscription;
  private aid: string;
  private un: string;

  public loading: boolean;
  public validLogin = true;
  public message: string;
  public loginData;
  public rememberMe: boolean;
  public displaySSOButton = false;
  public identityLogin = false;

  constructor(
    private auth: BcAuth,
    private sessionStorageService: CoolSessionStorage,
    private logoutSubscriber: LogoutSubscriber,
    private changeDetectorRef: ChangeDetectorRef,
    private stateService: StateServiceWrapper,
    private activatedRoute: ActivatedRoute) {
  }

  async ngOnInit() {
    this.validLogin = false;
    this.resetForm()
      .then(() => {
        if (this.validLogin) {
          this.stateService.go(bcStates.layout.url);
        }
      });

    this.logoutSubscription = this.logoutSubscriber.subscribe(this.resetForm.bind(this));
    this._getParamsFromUrl();

    if (this.aid || this.un) {
      this.displaySSOButton = true;
    }

    this.identityLogin = this.auth.isIdentityLoginAllowed();
    if (this.identityLogin) {
      const identityLoginConfiguration: IdentityLoginConfigurationModel = await this.auth.getIdentityLoginConfiguration();
      this.identityLogin = identityLoginConfiguration.enabled;
    }
  }

  ngOnDestroy() {
    if (this.logoutSubscription) {
      this.logoutSubscription.unsubscribe();
      this.logoutSubscription = null;
    }
  }

  public transitionToSSOLogin(): void {
    let ssoUrl: string = bcStates.sso.url;
    if (this.activatedRoute.snapshot.routeConfig.path !== ssoUrl) {
      if (this.aid) {
        ssoUrl = this._setSSOUrl(ssoUrl, 'account-id', this.aid);
      } else if (this.un) {
        ssoUrl = this._setSSOUrl(ssoUrl, 'username', this.un);
      }
      document.location.href = ssoUrl;
    }
  }

  public resetForm(): Promise<void> {
    this.loading = false;
    this.message = '';
    this.loginData = {
      username: '',
      password: ''
    };
    this.rememberMe = false;

    return this.setValidLogin();
  }

  public setValidLogin(): Promise<void> {
    const validLogin = this.sessionStorageService.getItem(bcSessionKeys.storage.key);
    if (validLogin) {
      this.validLogin = true;
      return Promise.resolve();
    }

    return this.auth.initForUser(null)
      .then(() => this.auth.isAuthenticated())
      .then((isAuthenticated) => {
        this.validLogin = isAuthenticated;
      })
      .catch(() => {
        this.validLogin = false;
      });
  }

  public login(loginData, rememberMe: boolean): void {
    if (this._isFormValid(loginData)) {
      this.loading = true;
      this.changeDetectorRef.detectChanges();
      this.auth.login(loginData, rememberMe)
        .then(() => {
          this._setLoginSuccess();
          this.stateService.go(bcStates.layout.url);
        })
        .catch(() => {
          this._setLoginError();
        });
    } else {
      this.message = 'bcLogin.messages.fillRequiredFields';
    }
  }

  private _isFormValid(loginData): boolean {
    return loginData && loginData.username && loginData.password;
  }

  private _setLoginError(): void {
    this.message = 'bcLogin.messages.loginError';
    this.changeDetectorRef.detectChanges();
    this.validLogin = false;
    this.loading = false;
  }

  private _setLoginSuccess(): void {
    this.message = 'bcLogin.messages.successLoginMessage';
    this.validLogin = true;
    this.loading = false;
  }

  private _getParamsFromUrl(): void {
    if (this.stateService.queryParams.aid) {
      this.aid = this.stateService.queryParams.aid;
    }
    if (this.stateService.queryParams.un) {
      this.un = this.stateService.queryParams.un;
    }
  }

  private _setSSOUrl(ssoUrl: string, type: string, value: string): string {
    let finalSSOUrl: string;
    finalSSOUrl = ssoUrl.replace(':paramType', type);
    finalSSOUrl = finalSSOUrl.replace(':paramValue', value);
    return finalSSOUrl;
  }
}
